import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { MESSAGES } from "../../../common/Messages";
import { isValidGuid } from "../../../common/Utils";
import BottomGrid from "../common/BottomGrid";
import TotalsTab from "./TotalsTab";
import {
    BOTTOM_TABS,
    BOTTOM_TAB_VISIBILITY,
    DEFAULT_LAYOUT_NAME,
    DRIVER_TAB_TITLES,
    DRIVER_TAB_TITLE_DEFAULT,
    GRID_KEYS,
    KEY_FIELD,
    ROW_SCOPED_BOTTOM_TABS,
} from "../constants";
import {
    BOTTOM_DISPATCH_COLUMNS,
    CONTENTS_COLUMNS,
    DETAILS_COLUMNS,
    DRIVERS_COLUMNS,
    EMPTY_CONTAINER_COLUMNS,
    HIST_SHIP_COLUMNS,
    MESSAGES_COLUMNS,
    SHIPPERS_COLUMNS,
    TRAILERS_COLUMNS,
    TRIPS_COLUMNS,
    TRUCKS_COLUMNS,
} from "./Columns";
import {
    getBottomDispatchObjectsStore,
    getEmptyContainerDispatchObjectsStore,
    getBottomTripsStore,
    getDispatchMessagesStore,
    getDispatchObjectContentsStore,
    getDispatchObjectDetailsStore,
    getDriversStore,
    getHistoricalDispatchObjectsStore,
    getHistoricalTripsStore,
    getShippersStore,
    getTrailersStore,
    getTrucksStore,
} from "../services/DispatchManagementService";

/**
 * Tab captions, matching the Delphi tab captions with the accelerator ampersands removed.
 *
 * "Empty Container" and "Hist Ship" / "Hist Trip" are the Delphi's own wording, not expansions of it.
 */
const TAB_TITLES = {
    [BOTTOM_TABS.DETAILS]: "Details",
    [BOTTOM_TABS.EMPTY_CONTAINER]: "Empty Container",
    [BOTTOM_TABS.CONTENTS]: "Contents",
    [BOTTOM_TABS.PICK]: "Pick",
    [BOTTOM_TABS.DEL]: "Del",
    [BOTTOM_TABS.TRIPS]: "Trips",
    [BOTTOM_TABS.TRUCKS]: "Trucks",
    [BOTTOM_TABS.TRAILERS]: "Trailers",
    // Overridden per profile - see DRIVER_TAB_TITLES and tabTitle below. The entry stays so the map
    // is complete and a profile-less board still has a caption for this tab.
    [BOTTOM_TABS.DRIVERS]: DRIVER_TAB_TITLE_DEFAULT,
    [BOTTOM_TABS.SHIPPERS]: "Shippers",
    [BOTTOM_TABS.MESSAGES]: "Messages",
    [BOTTOM_TABS.HIST_SHIP]: "Hist Ship",
    [BOTTOM_TABS.HIST_TRIP]: "Hist Trip",
    [BOTTOM_TABS.TOTALS]: "Totals",
};

/**
 * The board's bottom pane: a tab strip over the selected top row.
 *
 * Uses the same DevExtreme TabPanel as the top pane, so both strips look and behave alike -
 * keyboard navigation, overflow handling and theming come from the component rather than from
 * hand-rolled CSS.
 *
 * `deferRendering` is what makes fourteen tabs affordable: DevExtreme renders only the selected
 * tab's content, so switching to Trucks does not also fire off requests for Messages, Hist Trip and
 * the other eleven. Visited tabs stay mounted, so going back to one does not re-query it.
 *
 * Which tabs appear is driven by the active top tab through BOTTOM_TAB_VISIBILITY, the table form of
 * uFMD0000.pas SetBottomTabVisible.
 *
 * Three kinds of tab live here, and the difference matters:
 *
 *  - **Row-scoped** (Details, Contents, Messages) re-query when the selected top row changes, and
 *    render a placeholder with no grid mounted at all when nothing is selected. That is deliberate:
 *    mounting a keyed grid or setting a focusedRowKey against a missing parent throws DevExtreme
 *    E1042 (Architecture.md rule 5).
 *  - **Reference lists** (everything else with a grid) are not scoped to the selection. In the Delphi
 *    they are what a dispatcher drags between the panes, and pagBottomDispatchChange just reopens
 *    them. They render with nothing selected.
 *  - **Totals** is a form, computed from the selection on the client.
 */
const BottomTabs = (props) => {
    const { accessToken, topTab, profile, selectedRow, selectedRows, currentUser, dynamicFormSetting } = props;

    /**
     * One tab's caption.
     *
     * Only Drivers is not a constant: FMDISPATCHINGDP.DRIVERTABDEFAULT = 'C' renames it "Carrier"
     * (uFMD0000.pas:10687, which assigns tabDriversTop.Caption and tabBottomDrivers.Caption
     * together). Anything else, and a board with no profile, leaves it "Drivers".
     */
    const tabTitle = useCallback(
        (tab) =>
            tab === BOTTOM_TABS.DRIVERS
                ? DRIVER_TAB_TITLES[profile?.DriverTabDefault] ?? DRIVER_TAB_TITLE_DEFAULT
                : TAB_TITLES[tab],
        [profile]
    );

    const visibility = BOTTOM_TAB_VISIBILITY[topTab] ?? { visible: [], landing: null };
    const visibleTabs = visibility.visible;

    const landingIndex = useMemo(() => {
        const index = visibleTabs.indexOf(visibility.landing);
        return index >= 0 ? index : 0;
    }, [visibleTabs, visibility.landing]);

    const [selectedIndex, setSelectedIndex] = useState(landingIndex);
    const [layoutName, setLayoutName] = useState(DEFAULT_LAYOUT_NAME);

    // Switching top tabs returns the bottom pane to that tab's landing tab, as the Delphi does when it
    // assigns mgtBottom.ActivePage in SetBottomTabVisible. Keyed on topTab, not on landingIndex: the
    // three top tabs currently share a landing index, so watching the index alone would leave the
    // bottom pane on whatever tab the user had opened.
    useEffect(() => {
        setSelectedIndex(landingIndex);
    }, [topTab, landingIndex]);

    const dispatchObjectRowId = selectedRow?.RowId;

    // A parent key a row-scoped grid can actually be mounted against (Architecture.md rule 5).
    // isValidGuid is the shared helper - it rejects null, blank and the empty Guid, case-insensitively.
    const hasParent = isValidGuid(dispatchObjectRowId);

    // Row-scoped stores. Keyed on the selected row so their loadParams close over the current
    // selection rather than the one they were first built with.
    const detailsStore = useMemo(
        () => (hasParent ? getDispatchObjectDetailsStore(accessToken, selectedRow) : null),
        [accessToken, selectedRow, hasParent]
    );

    const contentsStore = useMemo(
        () => (hasParent ? getDispatchObjectContentsStore(accessToken, dispatchObjectRowId) : null),
        [accessToken, dispatchObjectRowId, hasParent]
    );

    // Messages is row-scoped too, unlike the reference lists below it: uFMM0042 takes the selected
    // dispatch object as a parameter and uFMD0000.pas:4840 re-parameterises it on every board-row
    // scroll.
    const messagesStore = useMemo(
        () => (hasParent ? getDispatchMessagesStore(accessToken, dispatchObjectRowId) : null),
        [accessToken, dispatchObjectRowId, hasParent]
    );

    // Reference lists. Built once per token - they do not depend on the selection.
    const referenceStores = useMemo(
        () => ({
            [BOTTOM_TABS.PICK]: getBottomDispatchObjectsStore(accessToken),
            // Same endpoint as Pick: in the Delphi the two bottom tabs list the same rows, and only
            // their default column order differs.
            [BOTTOM_TABS.DEL]: getBottomDispatchObjectsStore(accessToken),
            // Its own record set (RECORDTYPE 'C'), not the live list Pick and Del show.
            [BOTTOM_TABS.EMPTY_CONTAINER]: getEmptyContainerDispatchObjectsStore(accessToken),
            [BOTTOM_TABS.HIST_SHIP]: getHistoricalDispatchObjectsStore(accessToken),
            [BOTTOM_TABS.TRIPS]: getBottomTripsStore(accessToken),
            [BOTTOM_TABS.HIST_TRIP]: getHistoricalTripsStore(accessToken),
            [BOTTOM_TABS.TRUCKS]: getTrucksStore(accessToken),
            [BOTTOM_TABS.TRAILERS]: getTrailersStore(accessToken),
            [BOTTOM_TABS.DRIVERS]: getDriversStore(accessToken),
            [BOTTOM_TABS.SHIPPERS]: getShippersStore(accessToken),
        }),
        [accessToken]
    );

    /** Everything a BottomGrid needs beyond its store, key and columns. */
    const gridProps = useMemo(
        () => ({
            accessToken,
            currentLayout: layoutName,
            setCurrentLayout: setLayoutName,
        }),
        [accessToken, layoutName]
    );

    const renderTab = useCallback(
        (tab) => {
            // Row-scoped tabs with nothing selected: placeholder, no grid.
            if (ROW_SCOPED_BOTTOM_TABS.includes(tab) && !hasParent) {
                return <div className="dm-placeholder">{MESSAGES.DISPATCH_SELECT_RECORD}</div>;
            }

            switch (tab) {
                case BOTTOM_TABS.DETAILS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={detailsStore}
                            keyExpr={KEY_FIELD.LEGACY_ROW_ID}
                            gridKey={GRID_KEYS.DETAILS}
                            columns={DETAILS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.CONTENTS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={contentsStore}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.CONTENTS}
                            columns={CONTENTS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.EMPTY_CONTAINER:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.EMPTY_CONTAINER]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.EMPTY_CONTAINER}
                            columns={EMPTY_CONTAINER_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.PICK:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.PICK]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_PICK}
                            columns={BOTTOM_DISPATCH_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.DEL:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.DEL]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_DEL}
                            columns={BOTTOM_DISPATCH_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.HIST_SHIP:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.HIST_SHIP]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.HIST_SHIP}
                            columns={HIST_SHIP_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.TRIPS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.TRIPS]}
                            keyExpr={KEY_FIELD.LEGACY_ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_TRIPS}
                            columns={TRIPS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.HIST_TRIP:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.HIST_TRIP]}
                            keyExpr={KEY_FIELD.LEGACY_ROW_ID}
                            gridKey={GRID_KEYS.HIST_TRIP}
                            columns={TRIPS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.TRUCKS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.TRUCKS]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_TRUCKS}
                            columns={TRUCKS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.TRAILERS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.TRAILERS]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_TRAILERS}
                            columns={TRAILERS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.DRIVERS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.DRIVERS]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_DRIVERS}
                            columns={DRIVERS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.SHIPPERS:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={referenceStores[BOTTOM_TABS.SHIPPERS]}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_SHIPPERS}
                            columns={SHIPPERS_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.MESSAGES:
                    return (
                        <BottomGrid
                            {...gridProps}
                            dataSource={messagesStore}
                            keyExpr={KEY_FIELD.ROW_ID}
                            gridKey={GRID_KEYS.BOTTOM_MESSAGES}
                            columns={MESSAGES_COLUMNS}
                        />
                    );
                case BOTTOM_TABS.TOTALS:
                    return (
                        <TotalsTab
                            selectedRows={selectedRows}
                            currentUser={currentUser}
                            dynamicFormSetting={dynamicFormSetting}
                        />
                    );
                default:
                    return null;
            }
        },
        [
            gridProps,
            hasParent,
            detailsStore,
            contentsStore,
            messagesStore,
            referenceStores,
            selectedRows,
            currentUser,
            dynamicFormSetting,
        ]
    );

    return (
        <TabPanel
            selectedIndex={selectedIndex}
            onSelectedIndexChange={setSelectedIndex}
            height="100%"
            // Only the selected tab's content is rendered, so fourteen tabs do not mean fourteen
            // requests. Visited tabs stay mounted and are not re-queried on return.
            deferRendering={true}
            // Fourteen captions do not fit at every width. The nav buttons and drag-scrolling come
            // from the component; the hand-rolled strip this replaced just wrapped onto two rows.
            showNavButtons={true}
            scrollByContent={true}
        >
            {visibleTabs.map((tab) => (
                <TabItem key={tab} title={tabTitle(tab)}>
                    {renderTab(tab)}
                </TabItem>
            ))}
        </TabPanel>
    );
};

export default BottomTabs;
